import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { V2Button, V2Card, V2EmptyState, Icon } from '@sakhtar/design-system/react';
import { useCapabilities } from '../../lib/capabilities.js';
import { faDigits } from '../../lib/faDigits.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { MASTER_DATA_ENTITIES, type MasterDataEntityKind } from '../../lib/masterDataConfig.js';
import { useApiGet, type ApiGetState } from '../../lib/useApiGet.js';
import type { MasterDataListResponse } from '../../lib/types/masterData.js';
import './hub.css';

/**
 * UI_REFERENCE_SOURCE (primary, 2026-10-05): Sakhtar Cloud ERP v2.dc.html — frame `/t/kimia/master-data`
 *   v2 status: aligned (2026-10-11). Deviations from the v2 frame:
 *   - The reference file is TRUNCATED inside this frame: only the header
 *     (46px module mark with `box` icon, h1, subtitle, search input
 *     «جست‌وجو در اطلاعات پایه», secondary «ورود از اکسل») and the opening of
 *     the `repeat(auto-fill,minmax(200px,1fr))` card grid survive. The entity
 *     cards are completed here in the same v2 card language as the other hubs
 *     (module-tinted card + pattern wash, 26px icon chip, name, count,
 *     one-line description, فهرست/افزودن links) — not read from the frame.
 *   - «ورود از اکسل» is shown only when `useCapabilities().isTenantAdmin`
 *     (same gate as the Home «ورود داده از اکسل» shortcut, HomePage.tsx);
 *     the /data route itself still enforces permission server-side.
 *   - A card whose list request returns 403 is hidden (backend verdict, same
 *     as the CRM/Finance/Procurement hubs); no client-side permission guess.
 *   - MasterDataModuleLayout (not editable from this file) still renders its
 *     own PageHeader + tab strip above this head, so the title shows twice;
 *     the layout needs the ModuleLayout-style head to remove the duplicate.
 *
 * Legacy citation follows.
 */
/**
 * UI_REFERENCE_SOURCE:
 * section: live design project (claude.ai/design/p/6c9c527c-4436-4be5-9301-40d25bc755b6),
 *   "Sakhtar Cloud ERP - Final.dc.html", frame `/t/kimia/master-data` —
 *   checked 2026-10-03 by reading the frame's own markup directly (not the
 *   repo's static export: docs/design-system/reference/Sakhtar Cloud ERP -
 *   Final.html is the 2026-09-17 export and only carries the Home frame —
 *   the live project has since grown 9 more top-level frames, this one
 *   included; see docs/design-system/UI_REFERENCE.md's staleness note).
 * secondary reference: Sakhtar Pilot P0 §2.2.6 "HUB LANDING" (generic
 *   archetype this frame is a concrete instance of).
 * canonical pattern: header (title + subtitle) + search + "ورود از اکسل"
 *   action, then a tile grid — one card per entity with icon, label, a
 *   live record count, a one-line description, and فهرست/افزودن actions.
 *
 * status: PARTIAL_REAL.
 * - Search filters the tile grid client-side by label/description. There
 *   is no cross-entity search endpoint to back a real query-as-you-type
 *   against record contents, so this searches tile metadata, not records
 *   — same honest scope as a page filter, not a fabricated capability.
 * - "ورود از اکسل" links to the existing `/data` route (ImportExportPage)
 *   — this repo centralizes import/export there, not per-page buttons.
 * - Per-tile counts are real: `GET .../master-data/:entity?limit=200`,
 *   reading `items.length` (the already-scope-filtered list this app's
 *   list page itself renders), not the response's `.total` field.
 *   `.total` was deliberately NOT used even though
 *   PRODUCTION_READINESS_AUDIT.md's 2026-09-17 entry proposes swapping the
 *   5 controllers' `total: visible.length` for the already-computed
 *   `page.total`: `page.total` is a DB-level count taken before
 *   `filterMasterRecords`'s row-level ABAC scope filter
 *   (`record-scope.ts:81-89`, `recordInScope` against
 *   `MASTER_RECORD_POLICIES`'s `filterableFields` — `record-policy.ts`).
 *   No seeded role currently defines a scoped grant on these entities, but
 *   the permission model supports one, and a tenant that configured one
 *   would have this hub tile reveal a count of records the viewer
 *   couldn't otherwise see existed. Reading `items.length` instead costs
 *   accuracy above the server's 200-row page cap (shown as "۲۰۰+") but
 *   never discloses more than the viewer's own list page already shows
 *   them. See the audit doc's corrected entry for the full writeup.
 * - The design's "آخرین تغییرات اطلاعات پایه" (recent master-data changes)
 *   table is NOT built — confirmed no backend support (grepped
 *   `apps/api/src/master-data/**` and `packages/audit/**`: no activity
 *   feed/query API exists for master-data entities, only the write-only
 *   `packages/audit` entry/redact helpers). KIT_CONFORMANT: recorded in
 *   the audit doc rather than faked with placeholder rows.
 * - `location` (مکان انبار) gets a 6th tile with no frame of its own in
 *   the design (it's a `warehouse` sub-resource there) — kept because
 *   it's a real, already-shipped entity (BUG-11); removing it to match
 *   the design's 5-card count would drop a real capability, which this
 *   repo's own convention treats as a regression, not a fix.
 * state reference: ready (per-tile count loading/error degrades to "…"/
 *   "—" without blocking the rest of the grid — one entity's 403 must not
 *   take down the whole hub)
 */

const COUNT_PAGE_LIMIT = 200; // server-side hard clamp, apps/api/src/master-data/repos.ts:52

function formatCount(state: ApiGetState<MasterDataListResponse>): string {
  if (state.status === 'loading') return '…';
  if (state.status === 'error') return '—';
  const n = state.data.items.length;
  return n >= COUNT_PAGE_LIMIT ? `${faDigits(COUNT_PAGE_LIMIT)}+` : faDigits(n);
}

/** Backend verdict: the viewer lacks `master-data.<entity>.read` — hide the card (same as CRM/Finance hubs). */
function isForbidden(state: ApiGetState<MasterDataListResponse>): boolean {
  return state.status === 'error' && state.error.status === 403;
}

export function MasterDataHubPage() {
  const { slug } = useParams<{ slug: string }>();
  const caps = useCapabilities();
  const [search, setSearch] = useState('');
  const base = slug ? tenantBasePath(slug) : '';

  const countPath = (pathSegment: string): string | null =>
    slug ? `${base}/api/v1/master-data/${pathSegment}?limit=${COUNT_PAGE_LIMIT}` : null;

  // Unrolled, not `.map()`-ed over `MASTER_DATA_ENTITIES` — hooks must be
  // called unconditionally in a fixed, textually-visible order; this repo's
  // tile set is fixed, so named calls below.
  const partyCount = useApiGet<MasterDataListResponse>(countPath('parties'), [base]);
  const productCount = useApiGet<MasterDataListResponse>(countPath('products'), [base]);
  const uomCount = useApiGet<MasterDataListResponse>(countPath('uoms'), [base]);
  const taxCount = useApiGet<MasterDataListResponse>(countPath('taxes'), [base]);
  const warehouseCount = useApiGet<MasterDataListResponse>(countPath('warehouses'), [base]);
  const locationCount = useApiGet<MasterDataListResponse>(countPath('locations'), [base]);
  const costCenterCount = useApiGet<MasterDataListResponse>(countPath('cost-centers'), [base]);
  const priceListCount = useApiGet<MasterDataListResponse>(countPath('price-lists'), [base]);

  if (!slug) return null;

  const countByKind: Record<MasterDataEntityKind, ApiGetState<MasterDataListResponse>> = {
    party: partyCount,
    product: productCount,
    uom: uomCount,
    tax: taxCount,
    warehouse: warehouseCount,
    location: locationCount,
    cost_center: costCenterCount,
    price_list: priceListCount,
  };

  const permittedEntities = MASTER_DATA_ENTITIES.filter(
    (entity) => !isForbidden(countByKind[entity.kind]),
  );
  const normalizedSearch = search.trim().toLowerCase();
  const visibleEntities = normalizedSearch
    ? permittedEntities.filter(
        (entity) =>
          entity.labelPlural.toLowerCase().includes(normalizedSearch) ||
          entity.label.toLowerCase().includes(normalizedSearch) ||
          entity.hubDescription.toLowerCase().includes(normalizedSearch),
      )
    : permittedEntities;

  return (
    <div className="md-hub">
      <div className="md-hub__head">
        <div className="md-hub__tools">
          <div className="sk-input-group md-hub__search">
            <Icon name="search" size="sm" />
            <input
              type="search"
              aria-label="جست‌وجو در اطلاعات پایه"
              placeholder="جست‌وجو در اطلاعات پایه"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          {caps.isTenantAdmin ? (
            <Link to={`${base}/data`} className="md-hub__import">
              <V2Button variant="secondary">ورود از اکسل</V2Button>
            </Link>
          ) : null}
        </div>
      </div>

      {visibleEntities.length === 0 ? (
        <V2Card>
          <V2EmptyState
            icon={normalizedSearch ? 'search' : 'lock'}
            title={
              normalizedSearch ? 'موردی با این جست‌وجو پیدا نشد' : 'دسترسی به اطلاعات پایه ندارید'
            }
            description={
              normalizedSearch
                ? 'عبارت دیگری را امتحان کنید یا جست‌وجو را پاک کنید.'
                : 'برای مشاهدهٔ موجودیت‌های پایه با مدیر سازمان تماس بگیرید.'
            }
            compact
          />
        </V2Card>
      ) : (
        <div className="md-hub__grid">
          {visibleEntities.map((entity) => (
            <div key={entity.kind} className="md-hub-card">
              <Link to={`${base}/master-data/${entity.pathSegment}`} className="md-hub-card__main">
                <span className="md-hub-card__top">
                  <span className="md-hub-card__chip" aria-hidden="true">
                    <Icon name={entity.icon} size="sm" />
                  </span>
                  <span className="md-hub-card__name">{entity.labelPlural}</span>
                </span>
                <span className="md-hub-card__count">{formatCount(countByKind[entity.kind])}</span>
                <span className="md-hub-card__desc">{entity.hubDescription}</span>
              </Link>
              <div className="md-hub-card__foot">
                <Link
                  to={`${base}/master-data/${entity.pathSegment}`}
                  className="md-hub-card__link"
                >
                  فهرست
                </Link>
                <Link
                  to={`${base}/master-data/${entity.pathSegment}/new`}
                  className="md-hub-card__link"
                >
                  <Icon name="plus" size="sm" />
                  افزودن
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
